import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoComplete, Button, Checkbox, DatePicker, Input, InputNumber, Modal, Radio, Select, message } from 'antd';
import dayjs from 'dayjs';
import { getHr, hrApi, useHr } from './hrStore';
import {
  CONTRACT_KIND, EDUCATION, FIXED_MAX_TIMES, LEAVE_REASONS, LEAVE_TYPE, PROBATION_MIN_RATE, REQ_REASON, SOURCE, addDays, addMonths,
  branchById, candidateDupCheck, deptById, deptOf, empById, empContracts, empProbationLimit, fixedCount, fmtD, fmtM, fmtMoney, isWorking,
  noticeShortfall, planStatus, positionById, probationEndOf, requiredNotice, suggestNext, toDay, validateContract, validateRequisition,
  LEAVE_KIND, leaveBalance, validateLeave, workingDaysBetween, type LeaveKind, CONSENT_CHANNELS, CONSENT_TEXT,
  type ContractDraft, type ContractKind, type Education, type LeaveType, type ReqDraft, type ReqReason, type Source,
} from './hrLogic';
import { Field, Notes, useHrPerms } from './hrUi';

const DATE_FMT = 'DD/MM/YYYY';
export const DateInput = ({ value, onChange, min, disabled }: { value?: string; onChange: (v: string) => void; min?: string; disabled?: boolean }) => (
  <DatePicker className="w-full" format={DATE_FMT} placeholder="Chọn ngày" value={value ? dayjs(value) : null} disabled={disabled}
    disabledDate={min ? d => d.format('YYYY-MM-DD') < min : undefined}
    onChange={v => onChange(v ? v.format('YYYY-MM-DD') : '')} />
);
export const MoneyInput = ({ value, onChange, placeholder }: { value?: number; onChange: (v: number) => void; placeholder?: string }) => (
  <InputNumber className="!w-full" min={0} step={500000} value={value || null} placeholder={placeholder} suffix="₫"
    formatter={v => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')} parser={v => Number(`${v ?? ''}`.replace(/\D/g, '')) as unknown as 0}
    onChange={v => onChange(Number(v) || 0)} />
);

const endFor = (kind: ContractKind, start: string, months = 12, lim?: { days: number; working: boolean }) => {
  if (!start) return undefined;
  if (kind === 'indefinite') return undefined;
  if (kind === 'probation' && lim) return probationEndOf(start, lim);
  return addDays(addMonths(start, kind === 'service' ? 6 : months), -1);
};

/* ============================== Hợp đồng ============================== */

export function ContractModal({ empId: fixedEmp, init, onClose, onSaved }: { empId?: string; init?: Partial<ContractDraft>; onClose: () => void; onSaved?: (id: string) => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const [empId, setEmpId] = useState(fixedEmp || init?.empId || '');
  const emp = empById(s, empId);
  const base = (id: string): ContractDraft => {
    const e = empById(s, id);
    const sug = e ? suggestNext(s, e, today) : { kind: 'probation' as const, start: today, salary: 0 };
    return { empId: id, ...sug, ...(id === (fixedEmp || init?.empId) ? init : {}) } as ContractDraft;
  };
  const [d, setD] = useState<ContractDraft>(() => base(empId));
  const [signed, setSigned] = useState(false);
  const [signDate, setSignDate] = useState(today);
  const patch = (p: Partial<ContractDraft>) => setD(x => ({ ...x, ...p }));
  const lim = emp ? empProbationLimit(s, emp) : undefined;
  const nFixed = emp ? fixedCount(s, emp.id, d.id) : 0;
  const hasOfficial = emp ? empContracts(s, emp.id).some(c => c.kind === 'fixed' || c.kind === 'indefinite') : false;
  const v = useMemo(() => validateContract(s, { ...d, signedAt: signed ? signDate : undefined }), [s, d, signed, signDate]);

  const setKind = (kind: ContractKind) => {
    const official = d.kind === 'probation' ? d.officialSalary || d.salary : d.salary;
    patch({
      kind, end: endFor(kind, d.start, 12, lim),
      ...(kind === 'probation' ? { officialSalary: official, salary: Math.ceil((official * PROBATION_MIN_RATE) / 100000) * 100000 } : { salary: official, officialSalary: undefined }),
    });
  };
  const save = () => {
    const r = hrApi.saveContract({ ...d, signedAt: signed ? signDate : undefined }, me);
    if (!r.ok) { Modal.error({ title: 'Chưa lưu được hợp đồng', content: r.errors?.join('\n') }); return; }
    message.success(signed ? 'Đã lưu hợp đồng (đã ký)' : 'Đã lập hợp đồng — nhớ cập nhật khi ký');
    onSaved?.(r.id!);
    onClose();
  };

  return (
    <Modal open title={d.id ? 'Sửa hợp đồng' : 'Lập hợp đồng / thỏa thuận thử việc'} onCancel={onClose} width={640}
      footer={[<Button key="c" onClick={onClose}>Đóng</Button>, <Button key="s" type="primary" disabled={v.errors.length > 0} onClick={save}>Lưu</Button>]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {!fixedEmp && (
          <Field label="Nhân sự" className="sm:col-span-2">
            <Select showSearch className="w-full" value={empId || undefined} placeholder="Chọn nhân sự" optionFilterProp="label"
              options={s.employees.filter(e => isWorking(e, today) || e.hireDate > today).map(e => ({ value: e.id, label: `${e.name} · ${e.code} · ${positionById(s, e.positionId)?.name}` }))}
              onChange={id => { setEmpId(id); setD(base(id)); }} />
          </Field>
        )}
        {emp && (
          <div className="sm:col-span-2 text-xs text-gray-500 bg-gray-50 dark:bg-gray-800/60 rounded-lg px-3 py-2">
            <b className="text-gray-800 dark:text-gray-200">{emp.name}</b> · {positionById(s, emp.positionId)?.name} · {branchById(s, emp.branchId)?.short} · vào làm {fmtD(emp.hireDate)}
            <div>Đã ký {nFixed}/{FIXED_MAX_TIMES} HĐ xác định thời hạn · thử việc: {lim?.label}</div>
          </div>
        )}
        <Field label="Loại hợp đồng" className="sm:col-span-2">
          <Select className="w-full" value={d.kind} onChange={setKind}
            options={(Object.keys(CONTRACT_KIND) as ContractKind[]).map(k => ({
              value: k, label: CONTRACT_KIND[k].label,
              disabled: (k === 'fixed' && nFixed >= FIXED_MAX_TIMES) || (k === 'probation' && hasOfficial),
            }))} />
        </Field>
        <Field label="Ngày hiệu lực">
          <DateInput value={d.start} onChange={start => patch({ start, end: endFor(d.kind, start, d.end && d.start ? Math.max(1, Math.round(dayjs(d.end).diff(dayjs(d.start), 'month', true))) : 12, lim) })} />
        </Field>
        <Field label="Ngày kết thúc" hint={d.kind === 'fixed' ? (
          <span className="space-x-2">{[12, 24, 36].map(m => <button key={m} type="button" className="text-[#3e91ab] hover:underline" onClick={() => patch({ end: endFor('fixed', d.start, m) })}>{m} tháng</button>)}</span>
        ) : d.kind === 'probation' ? lim?.label : undefined}>
          <DateInput value={d.end} disabled={d.kind === 'indefinite'} min={d.start} onChange={end => patch({ end: end || undefined })} />
        </Field>
        {d.kind === 'probation' && (
          <Field label="Lương chính thức đã thỏa thuận">
            <MoneyInput value={d.officialSalary} onChange={officialSalary => patch({ officialSalary })} />
          </Field>
        )}
        <Field label={d.kind === 'probation' ? `Lương thử việc (≥ ${PROBATION_MIN_RATE * 100}%)` : d.kind === 'service' ? 'Thù lao / tháng' : 'Lương'}>
          <MoneyInput value={d.salary} onChange={salary => patch({ salary })} />
        </Field>
        <Field label="Ghi chú" className="sm:col-span-2">
          <Input value={d.note} onChange={e => patch({ note: e.target.value })} placeholder="VD: điều chỉnh lương sau đánh giá năm" />
        </Field>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
          <Checkbox checked={signed} onChange={e => setSigned(e.target.checked)}>Hai bên đã ký</Checkbox>
          {signed && <div className="w-[160px]"><DateInput value={signDate} onChange={setSignDate} /></div>}
          {!signed && <span className="text-[11px] text-gray-400">Chưa ký thì HĐ được lưu nháp, hệ thống nhắc ký trước ngày hiệu lực</span>}
        </div>
        <div className="sm:col-span-2"><Notes errors={v.errors} warnings={v.warnings} /></div>
      </div>
    </Modal>
  );
}

/* ============================== Đánh giá thử việc ============================== */

export function EvalModal({ contractId, onClose, onPassed }: { contractId: string; onClose: () => void; onPassed?: (empId: string) => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const c = s.contracts.find(x => x.id === contractId);
  const e = empById(s, c?.empId);
  const today = toDay();
  const [result, setResult] = useState<'pass' | 'fail'>('pass');
  const [score, setScore] = useState<number | null>(8);
  const [note, setNote] = useState('');
  const [lastDay, setLastDay] = useState(c?.end && c.end > today ? c.end : today);
  if (!c || !e) return null;
  const save = () => {
    const r = hrApi.evaluateProbation(c.id, result, me, { score: score || undefined, note, lastDay });
    if (!r.ok) { Modal.error({ title: 'Chưa lưu được', content: r.errors?.join('\n') }); return; }
    message.success(result === 'pass' ? 'Đã ghi nhận đạt thử việc' : 'Đã ghi nhận không đạt và lập thủ tục nghỉ việc');
    onClose();
    if (result === 'pass') onPassed?.(e.id);
  };
  return (
    <Modal open title={`Đánh giá thử việc · ${e.name}`} onCancel={onClose} onOk={save} okText={result === 'pass' ? 'Đạt — lập HĐ chính thức' : 'Không đạt — lập thủ tục nghỉ'} cancelText="Đóng"
      okButtonProps={{ danger: result === 'fail' }}>
      <div className="space-y-3">
        <div className="text-xs text-gray-500">{positionById(s, e.positionId)?.name} · thử việc {fmtD(c.start)} – {fmtD(c.end)} · lương chính thức thỏa thuận {fmtMoney(c.officialSalary || 0)}</div>
        <Radio.Group value={result} onChange={x => setResult(x.target.value)} optionType="button" buttonStyle="solid"
          options={[{ value: 'pass', label: 'Đạt' }, { value: 'fail', label: 'Không đạt' }]} />
        <Field label="Điểm đánh giá (1–10)"><InputNumber min={1} max={10} value={score} onChange={setScore} /></Field>
        <Field label={result === 'fail' ? 'Lý do không đạt (bắt buộc, gửi cho người lao động)' : 'Nhận xét'}>
          <Input.TextArea rows={3} value={note} onChange={x => setNote(x.target.value)} placeholder={result === 'fail' ? 'VD: chưa đạt tay nghề laser sau 2 lần kiểm tra' : 'Điểm mạnh, điểm cần cải thiện'} />
        </Field>
        {result === 'fail' && <Field label="Ngày làm việc cuối"><DateInput value={lastDay} onChange={setLastDay} min={c.start} /></Field>}
        <div className="text-[11px] text-gray-400">Kết thúc thử việc phải thông báo kết quả cho người lao động. Đạt → ký HĐLĐ; không đạt → chấm dứt, không cần báo trước.</div>
      </div>
    </Modal>
  );
}

/* ============================== Nghỉ việc ============================== */

export function OffboardModal({ empId, defaultType = 'voluntary', onClose }: { empId: string; defaultType?: LeaveType; onClose: () => void }) {
  const s = useHr();
  const navigate = useNavigate();
  const { me, canRecruit } = useHrPerms();
  const e = empById(s, empId);
  const today = toDay();
  const [type, setType] = useState<LeaveType>(defaultType);
  const [noticeDate, setNoticeDate] = useState(today);
  const need = requiredNotice(s, empId, type, noticeDate);
  const [lastDay, setLastDay] = useState(() => addDays(today, need.days || 0));
  const [reason, setReason] = useState(type === 'contract_end' ? 'Hết hạn HĐ không gia hạn' : '');
  const [note, setNote] = useState('');
  const [noRehire, setNoRehire] = useState(false);
  if (!e) return null;
  const short = noticeShortfall(s, { empId, type, noticeDate, lastDay });
  const save = () => {
    const r = hrApi.startOffboarding({ empId, type, noticeDate, lastDay, reason, note: note || undefined, noRehire }, me);
    if (!r.ok) { Modal.error({ title: 'Chưa lập được thủ tục', content: r.errors?.join('\n') }); return; }
    message.success('Đã lập thủ tục nghỉ việc');
    onClose();
    const st = planStatus(getHr(), e.branchId, deptOf(s, e), today);
    if (canRecruit && st.uncovered > 0 && type !== 'probation_fail') {
      Modal.confirm({
        title: 'Tạo yêu cầu tuyển thay thế?', okText: 'Tạo yêu cầu', cancelText: 'Để sau',
        content: `${deptById(s, deptOf(s, e))?.name} · ${branchById(s, e.branchId)?.short} sẽ thiếu ${st.uncovered} người so với định biên.`,
        onOk: () => navigate(`/jobs-view?new=1&replace=${empId}`),
      });
    }
  };
  return (
    <Modal open title={`Thủ tục nghỉ việc · ${e.name}`} onCancel={onClose} onOk={save} okText="Lập thủ tục" cancelText="Đóng" width={560}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Hình thức" className="sm:col-span-2">
          <Select className="w-full" value={type} onChange={t => { setType(t); if (t === 'contract_end') setReason('Hết hạn HĐ không gia hạn'); }}
            options={(Object.keys(LEAVE_TYPE) as LeaveType[]).map(k => ({ value: k, label: LEAVE_TYPE[k] }))} />
        </Field>
        <Field label={type === 'voluntary' ? 'Ngày nhận đơn' : 'Ngày thông báo'}><DateInput value={noticeDate} onChange={setNoticeDate} /></Field>
        <Field label="Ngày làm việc cuối" hint={need.label}><DateInput value={lastDay} onChange={setLastDay} min={e.hireDate} /></Field>
        <Field label="Lý do" className="sm:col-span-2">
          <AutoComplete className="w-full" value={reason} onChange={setReason} options={LEAVE_REASONS.map(r => ({ value: r }))} placeholder="Chọn hoặc nhập lý do"
            filterOption={(input, o) => String(o?.value).toLowerCase().includes(input.toLowerCase())} />
        </Field>
        <Field label="Ghi chú" className="sm:col-span-2"><Input.TextArea rows={2} value={note} onChange={x => setNote(x.target.value)} /></Field>
        <Checkbox className="sm:col-span-2" checked={noRehire} onChange={x => setNoRehire(x.target.checked)}>Không tái tuyển (hệ thống chặn khi người này ứng tuyển lại)</Checkbox>
        <div className="sm:col-span-2 space-y-2">
          <Notes warnings={short ? [`Báo trước ${short.given} ngày — ${short.label}. Ngày nghỉ sớm nhất: ${fmtD(short.minLast)} (trừ khi hai bên thỏa thuận).`] : []} />
          <div className="text-[11px] text-gray-400">Sau ngày nghỉ: thanh toán đủ các khoản trong 14 ngày (hạn {fmtD(addDays(lastDay, 14))}), báo giảm BHXH và trả sổ.</div>
        </div>
      </div>
    </Modal>
  );
}

/* ============================== Yêu cầu tuyển dụng ============================== */

export function RequisitionModal({ id, init, onClose, onSaved }: { id?: string; init?: Partial<ReqDraft>; onClose: () => void; onSaved?: (id: string) => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const cur = s.requisitions.find(r => r.id === id);
  const [d, setD] = useState<ReqDraft>(() => {
    if (cur) return { ...cur };
    const p = positionById(s, init?.positionId);
    return {
      branchId: '', positionId: '', qty: 1, reason: 'replace' as ReqReason, salaryMin: p?.salaryMin || 0, salaryMax: p?.salaryMax || 0,
      deadline: addDays(today, 30), ...init,
    };
  });
  const patch = (p: Partial<ReqDraft>) => setD(x => ({ ...x, ...p }));
  const pos = positionById(s, d.positionId);
  const v = useMemo(() => validateRequisition(s, d, today), [s, d, today]);
  const st = pos && d.branchId ? planStatus(s, d.branchId, pos.deptId, today, d.id) : null;
  const candidatesToReplace = s.employees.filter(e => e.branchId === d.branchId && (!pos || deptOf(s, e) === pos.deptId) && e.leftDate && e.leftDate >= addDays(today, -120));
  const save = () => {
    const r = hrApi.saveRequisition(d, me);
    if (!r.ok) { Modal.error({ title: 'Chưa lưu được', content: r.errors?.join('\n') }); return; }
    message.success(cur ? 'Đã cập nhật và gửi duyệt lại' : 'Đã gửi yêu cầu tuyển dụng chờ duyệt');
    onSaved?.(r.id!);
    onClose();
  };
  return (
    <Modal open title={cur ? `Sửa yêu cầu ${cur.code}` : 'Yêu cầu tuyển dụng'} onCancel={onClose} width={640}
      footer={[<Button key="c" onClick={onClose}>Đóng</Button>, <Button key="s" type="primary" disabled={v.errors.length > 0} onClick={save}>Gửi duyệt</Button>]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Chi nhánh">
          <Select className="w-full" value={d.branchId || undefined} placeholder="Chọn chi nhánh" options={s.branches.map(b => ({ value: b.id, label: b.name }))}
            onChange={branchId => patch({ branchId, replaceEmpIds: [] })} />
        </Field>
        <Field label="Vị trí">
          <Select showSearch className="w-full" value={d.positionId || undefined} placeholder="Chọn vị trí" optionFilterProp="label"
            options={s.depts.map(dp => ({ label: dp.name, options: s.positions.filter(p => p.deptId === dp.id).map(p => ({ value: p.id, label: p.name })) }))}
            onChange={positionId => { const p = positionById(s, positionId)!; patch({ positionId, salaryMin: p.salaryMin, salaryMax: p.salaryMax, replaceEmpIds: [] }); }} />
        </Field>
        <Field label="Lý do tuyển">
          <Select className="w-full" value={d.reason} onChange={reason => patch({ reason })} options={(Object.keys(REQ_REASON) as ReqReason[]).map(k => ({ value: k, label: REQ_REASON[k] }))} />
        </Field>
        <Field label="Số lượng"><InputNumber className="!w-full" min={1} max={20} value={d.qty} onChange={qty => patch({ qty: qty || 1 })} /></Field>
        {d.reason === 'replace' && (
          <Field label="Thay thế cho" className="sm:col-span-2" hint={candidatesToReplace.length ? undefined : 'Không có nhân sự nghỉ gần đây ở bộ phận này'}>
            <Select mode="multiple" className="w-full" value={d.replaceEmpIds || []} onChange={replaceEmpIds => patch({ replaceEmpIds })} placeholder="Chọn người đã / sắp nghỉ"
              options={candidatesToReplace.map(e => ({ value: e.id, label: `${e.name} · ${positionById(s, e.positionId)?.name} · ${e.leftDate! < today ? 'nghỉ' : 'sẽ nghỉ'} ${fmtD(e.leftDate)}` }))} />
          </Field>
        )}
        <Field label="Lương từ"><MoneyInput value={d.salaryMin} onChange={salaryMin => patch({ salaryMin })} /></Field>
        <Field label="Lương đến"><MoneyInput value={d.salaryMax} onChange={salaryMax => patch({ salaryMax })} /></Field>
        <Field label="Hạn cần người"><DateInput value={d.deadline} onChange={deadline => patch({ deadline })} min={today} /></Field>
        <Field label="Ghi chú / yêu cầu"><Input value={d.note} onChange={x => patch({ note: x.target.value })} placeholder="Kinh nghiệm, chứng chỉ, ca làm…" /></Field>
        {st && (
          <div className="sm:col-span-2 grid grid-cols-4 gap-2 text-center text-xs">
            {[['Định biên', st.plan], ['Đang làm', st.actual - st.leaving], ['Đang tuyển', st.recruiting], ['Còn trống', Math.max(0, st.plan - (st.actual - st.leaving) - st.upcoming - st.recruiting)]].map(([l, n]) => (
              <div key={String(l)} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 py-2"><div className="text-gray-500">{l}</div><div className="text-base font-semibold">{n}</div></div>
            ))}
          </div>
        )}
        {pos && <div className="sm:col-span-2 text-[11px] text-gray-400">Khung lương chuẩn {pos.name}: {fmtM(pos.salaryMin)} – {fmtM(pos.salaryMax)}{pos.license ? ' · bắt buộc có giấy phép / chứng chỉ' : ''}</div>}
        <div className="sm:col-span-2"><Notes errors={v.errors} warnings={v.warnings} /></div>
      </div>
    </Modal>
  );
}

/* ============================== Ứng viên ============================== */

export function CandidateModal({ reqId, onClose, onSaved }: { reqId?: string; onClose: () => void; onSaved?: (id: string) => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const [f, setF] = useState({
    name: '', phone: '', email: '', cccd: '', gender: 'Nữ' as 'Nam' | 'Nữ', education: 'college' as Education, experienceYears: 0,
    expectedSalary: 0, source: 'facebook' as Source, requisitionId: reqId || '', note: '',
  });
  const [consent, setConsent] = useState<{ agreed: boolean; channel?: string }>({ agreed: false });
  const patch = (p: Partial<typeof f>) => setF(x => ({ ...x, ...p }));
  const dup = useMemo(() => (f.phone.replace(/\D/g, '').length >= 9 || f.cccd ? candidateDupCheck(s, { phone: f.phone, cccd: f.cccd }, today) : { errors: [], warnings: [] }), [s, f.phone, f.cccd, today]);
  const open = s.requisitions.filter(r => r.status === 'approved');
  const save = () => {
    const r = hrApi.addCandidate({
      ...f, email: f.email || undefined, cccd: f.cccd || undefined, note: f.note || undefined, expectedSalary: f.expectedSalary || undefined,
      consent: consent.agreed && consent.channel ? { at: Date.now(), by: me, channel: consent.channel } : undefined,
    }, me);
    if (!r.ok) { Modal.error({ title: 'Chưa thêm được ứng viên', content: r.errors?.join('\n') }); return; }
    message.success('Đã thêm ứng viên');
    onSaved?.(r.id!);
    onClose();
  };
  return (
    <Modal open title="Thêm ứng viên" onCancel={onClose} width={620}
      footer={[<Button key="c" onClick={onClose}>Đóng</Button>, <Button key="s" type="primary" disabled={dup.errors.length > 0 || !consent.agreed || !consent.channel} onClick={save}>Thêm</Button>]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Ứng tuyển vị trí" className="sm:col-span-2">
          <Select className="w-full" value={f.requisitionId || undefined} placeholder="Chọn yêu cầu đang tuyển" onChange={requisitionId => patch({ requisitionId })}
            options={open.map(r => ({ value: r.id, label: `${r.code} · ${positionById(s, r.positionId)?.name} · ${branchById(s, r.branchId)?.short}` }))} />
        </Field>
        <Field label="Họ và tên"><Input value={f.name} onChange={x => patch({ name: x.target.value })} /></Field>
        <Field label="Số điện thoại"><Input value={f.phone} onChange={x => patch({ phone: x.target.value })} /></Field>
        <Field label="CCCD (để kiểm trùng)"><Input value={f.cccd} onChange={x => patch({ cccd: x.target.value })} /></Field>
        <Field label="Email"><Input value={f.email} onChange={x => patch({ email: x.target.value })} /></Field>
        <Field label="Giới tính"><Select className="w-full" value={f.gender} onChange={gender => patch({ gender })} options={[{ value: 'Nữ', label: 'Nữ' }, { value: 'Nam', label: 'Nam' }]} /></Field>
        <Field label="Trình độ"><Select className="w-full" value={f.education} onChange={education => patch({ education })} options={(Object.keys(EDUCATION) as Education[]).map(k => ({ value: k, label: EDUCATION[k] }))} /></Field>
        <Field label="Số năm kinh nghiệm"><InputNumber className="!w-full" min={0} max={40} value={f.experienceYears} onChange={x => patch({ experienceYears: x || 0 })} /></Field>
        <Field label="Lương mong muốn"><MoneyInput value={f.expectedSalary} onChange={expectedSalary => patch({ expectedSalary })} /></Field>
        <Field label="Nguồn"><Select className="w-full" value={f.source} onChange={source => patch({ source })} options={(Object.keys(SOURCE) as Source[]).map(k => ({ value: k, label: SOURCE[k] }))} /></Field>
        <Field label="Ghi chú"><Input value={f.note} onChange={x => patch({ note: x.target.value })} /></Field>
        <div className="sm:col-span-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40 p-3 space-y-2">
          <Checkbox checked={consent.agreed} onChange={x => setConsent({ ...consent, agreed: x.target.checked })}>
            <span className="text-sm">{CONSENT_TEXT}</span>
          </Checkbox>
          <Select className="w-full" placeholder="Ứng viên xác nhận qua…" value={consent.channel} onChange={channel => setConsent({ agreed: true, channel })}
            options={CONSENT_CHANNELS.map(x => ({ value: x, label: x }))} />
          <div className="text-[11px] text-gray-400">Bắt buộc theo Nghị định 13/2023/NĐ-CP. Hồ sơ không trúng tuyển tự đề xuất xóa dữ liệu sau 12 tháng.</div>
        </div>
        <div className="sm:col-span-2"><Notes errors={dup.errors} warnings={dup.warnings} /></div>
      </div>
    </Modal>
  );
}


/* ============================== Nghỉ phép ============================== */

export function LeaveModal({ empId, onClose }: { empId: string; onClose: () => void }) {
  const s = useHr();
  const { me } = useHrPerms();
  const today = toDay();
  const e = empById(s, empId);
  const [r, setR] = useState<{ kind: LeaveKind; from: string; to: string; days: number; reason?: string }>({ kind: 'annual', from: today, to: today, days: 1 });
  if (!e) return null;
  const patch = (p: Partial<typeof r>) => setR(x => {
    const n = { ...x, ...p };
    if ((p.from || p.to) && n.from && n.to && n.to >= n.from) n.days = workingDaysBetween(n.from, n.to);
    return n;
  });
  const b = leaveBalance(s, e, r.from || today);
  const v = validateLeave(s, e, r, today);
  const save = () => {
    const res = hrApi.addLeave(empId, { ...r, reason: r.reason?.trim() || undefined }, me);
    if (!res.ok) { Modal.error({ title: 'Chưa ghi nhận được', content: res.errors?.join('\n') }); return; }
    message.success('Đã ghi nhận ngày nghỉ');
    onClose();
  };
  return (
    <Modal open title={`Ghi nhận nghỉ · ${e.name}`} onCancel={onClose} width={560}
      footer={[<Button key="c" onClick={onClose}>Đóng</Button>, <Button key="s" type="primary" disabled={v.errors.length > 0} onClick={save}>Ghi nhận</Button>]}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2 grid grid-cols-4 gap-2 text-center text-xs">
          {[['Phép năm', b.entitled], ['Năm trước chuyển', b.carried], ['Đã nghỉ', b.used], ['Còn lại', b.remain]].map(([l, n]) => (
            <div key={String(l)} className="rounded-lg bg-gray-50 dark:bg-gray-800/60 py-2"><div className="text-gray-500">{l}</div><div className={`text-base font-semibold ${l === 'Còn lại' && Number(n) <= 0 ? 'text-rose-600' : ''}`}>{n}</div></div>
          ))}
        </div>
        <Field label="Loại nghỉ" className="sm:col-span-2">
          <Select className="w-full" value={r.kind} onChange={kind => patch({ kind })} options={(Object.keys(LEAVE_KIND) as LeaveKind[]).map(k => ({ value: k, label: LEAVE_KIND[k].label }))} />
        </Field>
        <Field label="Từ ngày"><DateInput value={r.from} onChange={from => patch({ from, ...(from > r.to ? { to: from } : {}) })} /></Field>
        <Field label="Đến ngày"><DateInput value={r.to} onChange={to => patch({ to })} min={r.from} /></Field>
        <Field label="Số ngày" hint="Tự tính theo ngày làm việc (bỏ Chủ nhật); nhập 0,5 nếu nghỉ nửa ngày">
          <InputNumber className="!w-full" min={0.5} step={0.5} value={r.days} onChange={days => setR(x => ({ ...x, days: Number(days) || 0 }))} />
        </Field>
        <Field label="Lý do"><Input value={r.reason} onChange={x => setR(y => ({ ...y, reason: x.target.value }))} /></Field>
        <div className="sm:col-span-2"><Notes errors={v.errors} warnings={v.warnings} /></div>
      </div>
    </Modal>
  );
}
